import { useCallback, useEffect, useMemo, useState } from 'react'
import { App as AntdApp, Button, Card, Flex, Form, Input, Modal, Popconfirm, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { AuthUserRecord, AuthUsersResponse } from '../../shared/services/api'
import { useAuthContext } from '../../shared/contexts/AuthContext'
import { getApiService } from '../../shared/services/api'
import { MetricCard } from '../../shared/ui/MetricCard'
import { PageHeader } from '../../shared/ui/PageHeader'

type AuthUserForm = {
  email: string
  role: AuthUserRecord['role']
  is_active: boolean
  employee_name?: string | null
  department?: string | null
  position?: string | null
  password?: string
}

const AUTH_USERS_PAGE_SIZE = 25
const AUTH_USERS_PAGE_SIZE_OPTIONS = [25, 50, 100]

type AuthUsersPagination = {
  page: number
  pageSize: number
}

export function AuthUsersPage() {
  const { message } = AntdApp.useApp()
  const api = useMemo(() => getApiService(), [])
  const { user: currentUser } = useAuthContext()
  const [data, setData] = useState<AuthUsersResponse | null>(null)
  const [users, setUsers] = useState<AuthUserRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<AuthUserRecord | null>(null)
  const [creating, setCreating] = useState(false)
  const [pagination, setPagination] = useState<AuthUsersPagination>({
    page: 1,
    pageSize: AUTH_USERS_PAGE_SIZE,
  })
  const summary = data?.summary
  const currentUserEmail = currentUser?.email.toLowerCase()

  const load = useCallback(async (nextPagination = pagination) => {
    setLoading(true)
    try {
      const response = await api.listAuthUsers(nextPagination)
      setData(response.data)
      setUsers(response.data.users)
    } catch {
      message.error('Не удалось загрузить сотрудников')
    } finally {
      setLoading(false)
    }
  }, [api, pagination])

  const closeEditor = () => {
    setEditing(null)
    setCreating(false)
  }

  const deleteUser = async (user: AuthUserRecord) => {
    try {
      await api.deleteAuthUser(user.email)
      message.success('Сотрудник удален')
      await load()
    } catch (err) {
      message.error(getAuthUserErrorMessage(err, 'Не удалось удалить сотрудника'))
    }
  }

  const getDeleteDisabledReason = (user: AuthUserRecord) => {
    if (currentUserEmail === user.email.toLowerCase()) return 'Нельзя удалить текущего пользователя'
    if (user.role === 'admin' && user.is_active && (summary?.activeAdminCount ?? 0) <= 1) {
      return 'Нельзя удалить последнего активного администратора'
    }
    return null
  }

  useEffect(() => {
    void load(pagination)
  }, [load, pagination])

  const columns: ColumnsType<AuthUserRecord> = [
    {
      title: 'Действия',
      key: 'actions',
      width: 160,
      render: (_, user) => {
        const deleteDisabledReason = getDeleteDisabledReason(user)

        return (
          <Flex gap={8} align="center">
            <Tooltip title="Редактировать сотрудника">
              <Button
                type="primary"
                ghost
                size="small"
                icon={<EditOutlined />}
                aria-label={`Редактировать ${user.email}`}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setEditing(user)
                  setCreating(false)
                }}
              >
                Изменить
              </Button>
            </Tooltip>
            <Tooltip title={deleteDisabledReason || 'Удалить сотрудника'}>
              <Popconfirm
                title="Удалить сотрудника?"
                description={user.email}
                okText="Удалить"
                cancelText="Отмена"
                okButtonProps={{ danger: true }}
                disabled={Boolean(deleteDisabledReason)}
                onConfirm={() => deleteUser(user)}
              >
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  aria-label={`Удалить ${user.email}`}
                  disabled={Boolean(deleteDisabledReason)}
                />
              </Popconfirm>
            </Tooltip>
          </Flex>
        )
      },
    },
    {
      title: 'Адрес электронной почты',
      dataIndex: 'email',
      width: 280,
      render: (email, user) => (
        <Flex vertical gap={0}>
          <Typography.Text strong>{email}</Typography.Text>
          <Typography.Text className="auth-users-row-meta" type="secondary">
            ID {user.id}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      width: 130,
      render: (role) => <RoleTag role={role} />,
    },
    {
      title: 'Активен',
      dataIndex: 'is_active',
      width: 110,
      render: (value) => value ? <Tag color="success">Да</Tag> : <Tag color="error">Нет</Tag>,
    },
    {
      title: 'Сотрудник',
      dataIndex: 'employee_name',
      width: 190,
      render: (value) => value || <Typography.Text type="secondary">Не указан</Typography.Text>,
    },
    {
      title: 'Пароль',
      dataIndex: 'has_password',
      width: 120,
      render: (value) => value ? <Tag color="success">Задан</Tag> : <Tag>Нет</Tag>,
    },
    {
      title: 'Отдел',
      dataIndex: 'department',
      width: 150,
      render: (value) => value || <Typography.Text type="secondary">-</Typography.Text>,
    },
    {
      title: 'Должность',
      dataIndex: 'position',
      width: 160,
      render: (value) => value || <Typography.Text type="secondary">-</Typography.Text>,
    },
    {
      title: 'Последний вход',
      dataIndex: 'last_login_at',
      width: 190,
      render: (value) => value ? <span className="auth-users-date">{formatAuthUserDate(value)}</span> : <Typography.Text type="secondary">Не входил</Typography.Text>,
    },
  ]

  return (
    <Flex vertical gap="middle">
      <PageHeader
        title="Сотрудники и роли"
        description="Доступы, роли, пароли и состояние сотрудников."
        icon={<TeamOutlined />}
        extra={(
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null)
              setCreating(true)
            }}
          >
            Добавить сотрудника
          </Button>
        )}
      />

      <div className="metric-grid">
        <MetricCard title="Всего пользователей" value={summary?.count ?? 0} tone="blue" />
        <MetricCard title="Активные" value={summary?.activeCount ?? 0} tone="green" />
        <MetricCard title="Администраторы" value={summary?.adminCount ?? 0} tone="amber" />
        <MetricCard title="С паролем" value={summary?.passwordCount ?? 0} tone="blue" />
      </div>

      <Card className="table-card">
        <Table<AuthUserRecord>
          className="auth-users-table"
          rowKey="email"
          columns={columns}
          dataSource={users}
          loading={loading}
          rowClassName={(user) => user.is_active ? 'auth-user-row-active' : 'auth-user-row-inactive'}
          scroll={{ x: 1490 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            pageSizeOptions: AUTH_USERS_PAGE_SIZE_OPTIONS,
            showLessItems: true,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`,
            total: data?.count ?? 0,
            onChange: (page, pageSize) => {
              setPagination({ page, pageSize })
            },
          }}
        />
      </Card>

      <AuthUserEditor
        open={creating || Boolean(editing)}
        user={editing}
        onCancel={closeEditor}
        onSubmit={async (values) => {
          const patch: Parameters<typeof api.updateAuthUser>[1] = {
            role: values.role,
            is_active: values.is_active ? 1 : 0,
            employee_name: values.employee_name || null,
            department: values.department || null,
            position: values.position || null,
          }
          if (values.password) patch.password = values.password

          try {
            if (creating) {
              await api.createAuthUser({
                email: values.email.trim(),
                role: values.role,
                is_active: values.is_active ? 1 : 0,
                employee_name: values.employee_name || null,
                department: values.department || null,
                position: values.position || null,
                password: values.password || null,
              })
              message.success('Сотрудник добавлен')
            } else if (editing) {
              await api.updateAuthUser(editing.email, patch)
              message.success('Пользователь обновлен')
            }
            closeEditor()
            await load()
          } catch (err) {
            message.error(getAuthUserErrorMessage(err, 'Не удалось сохранить сотрудника'))
          }
        }}
      />
    </Flex>
  )
}

function formatAuthUserDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}

function RoleTag({ role }: { role: AuthUserRecord['role'] }) {
  const colors = {
    viewer: 'default',
    operator: 'processing',
    admin: 'success',
  }
  const labels = { viewer: 'Наблюдатель', operator: 'Оператор', admin: 'Администратор' }
  return <Tag color={colors[role]}>{labels[role]}</Tag>
}

function AuthUserEditor({
  open,
  user,
  onCancel,
  onSubmit,
}: {
  open: boolean
  user: AuthUserRecord | null
  onCancel: () => void
  onSubmit: (values: AuthUserForm) => Promise<void>
}) {
  const [form] = Form.useForm<AuthUserForm>()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    form.resetFields()
    if (user) {
      form.setFieldsValue({
        email: user.email,
        role: user.role,
        is_active: Boolean(user.is_active),
        employee_name: user.employee_name,
        department: user.department,
        position: user.position,
      })
    } else if (open) {
      form.setFieldsValue({
        role: 'viewer',
        is_active: true,
      })
    }
  }, [form, open, user])

  return (
    <Modal
      open={open}
      title={user ? user.email : 'Новый сотрудник'}
      okText={user ? 'Сохранить' : 'Добавить'}
      cancelText="Отмена"
      confirmLoading={submitting}
      forceRender
      onCancel={onCancel}
      onOk={() => {
        void form.validateFields().then(async (values) => {
          setSubmitting(true)
          try {
            await onSubmit(values)
          } finally {
            setSubmitting(false)
          }
        })
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Адрес электронной почты"
          name="email"
          rules={[
            { required: true, message: 'Введите адрес электронной почты' },
            { type: 'email', message: 'Введите правильный адрес электронной почты' },
          ]}
        >
          <Input autoComplete="username" disabled={Boolean(user)} maxLength={255} />
        </Form.Item>
        <Form.Item label="Роль" name="role" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'viewer', label: 'Наблюдатель' },
              { value: 'operator', label: 'Оператор' },
              { value: 'admin', label: 'Администратор' },
            ]}
          />
        </Form.Item>
        <Form.Item label="Активен" name="is_active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label="Сотрудник" name="employee_name">
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item label="Отдел" name="department">
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item label="Должность" name="position">
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          label="Новый пароль"
          name="password"
          rules={[{ min: 8, message: 'Минимум 8 символов' }]}
          extra={user?.has_password ? 'Оставьте пустым, чтобы не менять пароль.' : undefined}
        >
          <Input.Password autoComplete="new-password" maxLength={256} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

function getAuthUserErrorMessage(err: unknown, fallback: string) {
  const response = (err as { response?: { status?: number; data?: { message?: string } } } | null)?.response
  const messageText = response?.data?.message || ''

  if (response?.status === 409 && messageText.includes('already exists')) {
    return 'Сотрудник с таким email уже существует'
  }
  if (response?.status === 400 && messageText.includes('Current admin user cannot be deleted')) {
    return 'Нельзя удалить текущего администратора'
  }
  if (response?.status === 409 && messageText.includes('Last active admin user cannot be deleted')) {
    return 'Нельзя удалить последнего активного администратора'
  }
  if (response?.status === 404) return 'Сотрудник не найден'
  return fallback
}
